import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentNotifications } from '../../context/AgentNotificationContext';
import usePixieDustNavigation from '../../hooks/usePixieDustNavigation';
import NotificationSettings from './NotificationSettings';
import { 
  Bell, 
  X, 
  Check, 
  Trash2, 
  FileText, 
  Users, 
  Calendar,
  AlertCircle,
  Clock,
  CheckCircle,
  DollarSign,
  Settings
} from 'lucide-react';
import toast from '../../utils/toast';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    isConnected,
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useAgentNotifications();
  
  // Pixie dust navigation hook
  const { navigateToUnifiedItinerary } = usePixieDustNavigation();



  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notificationid);
    }

    // Handle navigation based on notification type - exactly like tripRow.jsx
    if (notification.data?.action) {
      switch (notification.data.action) {
        case 'view_itinerary':
          setIsOpen(false);
          navigateToUnifiedItinerary(notification.data.tripId);
          break;
        case 'view_trip':
          setIsOpen(false);
          navigate(`/trips`);
          break;
        case 'view_client':
          setIsOpen(false);
          navigate(`/clients`);
          break;
        case 'view_lead':
          setIsOpen(false);
          navigate(`/leads`);
          break;
        case 'view_commission':
          setIsOpen(false);
          navigate(`/commissions`);
          break;
        default:
          setIsOpen(false);
          break;
      }
    } else {
      setIsOpen(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'questionnaire_submitted':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'new_trip':
        return <Calendar className="w-4 h-4 text-green-500" />;
      case 'new_lead':
        return <Users className="w-4 h-4 text-purple-500" />;
      case 'lead_converted':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'new_commission':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'payment_processed':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'new_client':
        return <Users className="w-4 h-4 text-purple-500" />;
      case 'trip_status_changed':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'reminder':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'system':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    try {
      // Handle invalid or missing timestamp
      if (!timestamp) {
        return 'Unknown time';
      }
      
      const now = new Date();
      let notificationTime;
      
      // Handle different timestamp formats
      if (typeof timestamp === 'string') {
        // Handle PostgreSQL timestamp format: "2025-09-15 20:09:32.342392"
        if (timestamp.includes(' ')) {
          // Convert PostgreSQL timestamp to ISO format
          const isoString = timestamp.replace(' ', 'T') + 'Z';
          notificationTime = new Date(isoString);
        } else {
          // Handle ISO strings
          notificationTime = new Date(timestamp);
        }
      } else if (typeof timestamp === 'object' && timestamp !== null) {
        // Handle timestamp objects (check common properties)
        if (timestamp.createdat) {
          // Try createdat property
          notificationTime = new Date(timestamp.createdat);
        } else if (timestamp.createdAt) {
          // Try createdAt property
          notificationTime = new Date(timestamp.createdAt);
        } else if (timestamp.timestamp) {
          // Try timestamp property
          notificationTime = new Date(timestamp.timestamp);
        } else if (timestamp.date) {
          // Try date property
          notificationTime = new Date(timestamp.date);
        } else {
          // If it's an empty object or unknown structure, return fallback
          return 'Unknown time';
        }
      } else if (typeof timestamp === 'number') {
        // Handle Unix timestamps
        notificationTime = new Date(timestamp);
      } else {
        // Handle other types
        notificationTime = new Date(timestamp);
      }
      
      // Check if the date is valid
      if (isNaN(notificationTime.getTime())) {
        console.warn('Invalid date result:', notificationTime, 'from timestamp:', timestamp);
        return 'Unknown time';
      }
      
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    } catch (error) {
      console.error('Error formatting time ago:', error, 'Timestamp:', timestamp);
      return 'Unknown time';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1.5 text-gray-300 rounded-lg transition-colors duration-200 hover:text-white hover:bg-gray-700/50 sm:p-2"
        title={isConnected ? 'Real-time notifications active' : 'Notifications (offline mode)'}
      >
        <Bell className={`w-4 h-4 sm:w-5 sm:h-5 ${isConnected ? 'text-green-400' : 'text-gray-300'}`} />
        {unreadCount > 0 && (
          <span className="flex absolute -top-1 -right-1 justify-center items-center w-3 h-3 text-xs text-white bg-red-500 rounded-full sm:w-4 sm:h-4">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {/* Connection indicator */}
        <div className={`absolute -bottom-1 -right-1 w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-400'}`} />
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-2xl backdrop-blur-sm animate-scale-in">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-100">
            <div className="flex gap-2 items-center">
              <h3 className="text-sm font-semibold text-gray-900">
                Notifications
              </h3>
              <div className={`flex items-center gap-1 text-xs ${isConnected ? 'text-green-600' : 'text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-400'}`} />
                <span>{isConnected ? 'Live' : 'Offline'}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowSettings(true);
                }}
                className="text-gray-400 hover:text-gray-600"
                title="Notification Settings"
              >
                <Settings className="w-4 h-4" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-96">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <div className="w-6 h-6 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col justify-center items-center p-8 text-gray-500">
                <Bell className="mb-2 w-8 h-8 text-gray-300" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs text-gray-400">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.notificationid}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:shadow-sm hover:scale-[1.02] transform ${
                      !notification.is_read ? 'bg-blue-50/50 border-l-2 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex gap-3 items-start">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-2 justify-between items-start">
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${
                              !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                              {notification.message}
                            </p>
                            <div className="flex gap-2 items-center mt-2">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                {formatTimeAgo(notification.createdat)}
                              </span>
                              {!notification.is_read && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-1">
                            {!notification.is_read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.notificationid);
                                }}
                                className="p-1 text-gray-400 transition-colors hover:text-green-600"
                                title="Mark as read"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteNotification(e, notification.notificationid)}
                              className="p-1 text-gray-400 transition-colors hover:text-red-600"
                              title="Delete notification"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={() => navigate('/notifications')}
                className="w-full text-xs font-medium text-center text-blue-600 hover:text-blue-700"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Notification Settings Modal */}
      <NotificationSettings 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
      />
    </div>
  );
};

export default NotificationDropdown; 